import { reaction } from "mobx";
import React, { useContext, useEffect, useMemo } from "react";
import type {
  BillableApplicationProps,
  BillableUserProps,
  BillingAccountProps,
  BillingPlanProps,
  PaymentMethodProps,
  SpendLimitDetailsProps,
  UpcomingInvoiceProps,
} from "state-trees/src/Billing";
import { CachePolicies } from "use-http";
import { BillingSession } from "../../../client-state-trees/billing/BillingSession";
import { safelyRunAsyncEventHandler } from "../../../lib/utils";
import { useBillingFetch } from "../Billing";

const BillingContext = React.createContext<{
  loading: boolean;
  billingSession: BillingSession | null;
  applications: BillableApplicationProps[];
  billableUsers: BillableUserProps[];
  appInstallLimitReached: boolean;
}>(null as any);

export interface BillingPageProps {
  billingAccount: BillingAccountProps;
  billingPlan: BillingPlanProps;
  billingPlans: BillingPlanProps[];
  paymentMethod?: PaymentMethodProps;
  upcomingInvoice: UpcomingInvoiceProps;
  billableUsers: BillableUserProps[];
  applications: BillableApplicationProps[];
  appInstallLimitReached: boolean;
  spendLimitDetails?: SpendLimitDetailsProps;
}

export const BillingProvider = (props: { path?: string; children: React.ReactNode }) => {
  const {
    data: billingData,
    loading,
    error,
  } = useBillingFetch<BillingPageProps>(
    props.path ?? "/auth/api/billing",
    {
      cachePolicy: CachePolicies.CACHE_AND_NETWORK,
    },
    []
  );

  const billingSession = useMemo(() => {
    if (!billingData) return null;

    const {
      billingAccount: initialBillingAccount,
      billingPlan: initialBillingPlan,
      billingPlans: initialBillingPlans,
      paymentMethod: initialPaymentMethod,
      upcomingInvoice: initialUpcomingInvoice,
      spendLimitDetails: initialSpendLimitDetails,
    } = billingData;

    if (!initialBillingAccount && !initialBillingPlan && !initialUpcomingInvoice) {
      return null;
    }

    const session = BillingSession.create({
      paymentMethod: initialPaymentMethod,
      billingAccount: initialBillingAccount,
      billingPlan: initialBillingPlan,
      billingPlans: initialBillingPlans,
      upcomingInvoice: initialUpcomingInvoice,
    });

    if (initialSpendLimitDetails) {
      session.setSpendLimit(initialSpendLimitDetails);
      if (session.thresholdsAsArray.length === 0) {
        // Add a placeholder threshold
        session.addThreshold(-1);
      }
    } else {
      if (!initialBillingPlan.isFreePlan) {
        session.addThreshold(-1);
      }
    }

    return session;
  }, [billingData]);

  useEffect(() => {
    if (!billingSession) return;

    return reaction(
      () => billingSession.billingOperationInProgress,
      (isWorking) => {
        if (isWorking) {
          safelyRunAsyncEventHandler(async () => {
            await billingSession.waitForBillingOperationToSettle();
          });
        }
      }
    );
  }, [billingSession]);

  return (
    <BillingContext.Provider
      value={{
        loading,
        billingSession,
        applications: billingData?.applications ?? [],
        billableUsers: billingData?.billableUsers ?? [],
        appInstallLimitReached: billingData?.appInstallLimitReached ?? false,
      }}
    >
      {props.children}
    </BillingContext.Provider>
  );
};

export const useBilling = () => {
  const billingContext = useContext(BillingContext);

  if (!billingContext) {
    throw new Error("Billing context not found");
  }

  return billingContext;
};

export const useBillingSession = () => {
  const billingContext = useBilling();

  if (!billingContext.billingSession) {
    throw new Error("Billing session not found");
  }

  return billingContext.billingSession;
};
